import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Calendar,
  ArrowRight,
  CheckCircle2,
  Circle,
  Clock,
  Eye,
  EyeOff,
  FolderOpen,
  Layers,
  MoreVertical,
  PlayCircle,
  Settings,
  Trash2,
} from "lucide-react";

import { Avatar } from "../../../components/Avatar";
import { Badge } from "../../../components/Badge";
import { PROJECT_STATE_BADGES } from "../stateBadges";
import { ReadOnlyBadge } from "../../../components/ReadOnlyBadge";
import { Button } from "../../../components/Button";
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../components/DropdownMenu";
import { DataTable } from "../../../components/DataTable";
import { getAvatarImageUrl } from "../../../utils/avatarUtils";

const TABLE_COLUMNS = [
  { key: "project", label: "Proyecto", tooltip: "Nombre del proyecto" },
  { key: "client", label: "Cliente", tooltip: "Cliente asociado al proyecto" },
  { key: "state", label: "Estado", tooltip: "Estado actual del proyecto" },
  { key: "template", label: "Plantilla", tooltip: "Plantilla usada para generar el proyecto" },
  { key: "start", label: "Inicio", tooltip: "Fecha de inicio del proyecto" },
  { key: "created", label: "Creación", tooltip: "Fecha de creación del proyecto" },
  { key: "visibility", label: "Visibilidad", tooltip: "Si el cliente puede ver el proyecto" },
  { key: "progress", label: "Progreso", tooltip: "Porcentaje de avance del proyecto" },
  { key: "actions", label: "Acciones", tooltip: "Acciones disponibles para el proyecto" },
];

// Targets de los tutoriales de proyectos (la tabla es la vista que usan).
const TABLE_SLOT_PROPS = {
  toolbar: { "data-tutorial": "projects-toolbar" },
  tableContainer: { "data-tutorial": "projects-list" },
};


const formatDate = (value) => {
  if (!value) return "Sin fecha";

  try {
    return new Date(value).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

function ProjectActionsCell({ project, interactionProps, onViewProgress }) {
  const theme = useTheme();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuButtonEl, setMenuButtonEl] = useState(null);

  const {
    onStateChange,
    onDelete,
    onToggleVisibility,
    canToggleVisibility,
    onConfigureRecurrence,
    forceMenuOpen = false,
    menuButtonDataTutorial,
    recurrenceMenuItemDataTutorial,
    highlightRecurrenceMenuItem = false,
  } = interactionProps;
  // En el tutorial el menú se mantiene abierto anclado a su botón.
  const resolvedMenuAnchorEl = forceMenuOpen ? menuButtonEl : menuAnchorEl;
  const menuOpen = Boolean(resolvedMenuAnchorEl);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const hasMenuActions =
    Boolean(onStateChange) ||
    Boolean(onDelete) ||
    (Boolean(canToggleVisibility) && Boolean(onToggleVisibility)) ||
    Boolean(onConfigureRecurrence);

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      spacing={1}
      onClick={(event) => event.stopPropagation()}
    >
      <Tooltip title="Ver progreso del proyecto" arrow>
        <Box>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewProgress(project)}
            sx={{ whiteSpace: "nowrap" }}
          >
            <ArrowRight size={14} />
            Progreso
          </Button>
        </Box>
      </Tooltip>

      {hasMenuActions && (
        <>
          <Tooltip title="Ver acciones" arrow>
            <IconButton
              ref={setMenuButtonEl}
              data-tutorial={menuButtonDataTutorial}
              size="small"
              onClick={handleMenuOpen}
              sx={{
                width: "28px",
                height: "28px",
                padding: 0,
                flexShrink: 0,
              }}
            >
              <MoreVertical size={16} />
            </IconButton>
          </Tooltip>

          <DropdownMenuContent
            anchorEl={resolvedMenuAnchorEl}
            open={menuOpen}
            onClose={forceMenuOpen ? () => undefined : handleMenuClose}
            align="end"
            onClick={(event) => event.stopPropagation()}
          >
            {onStateChange && project.state !== "in_progress" && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClose();
                  onStateChange(project.id, "in_progress", project.state);
                }}
              >
                <PlayCircle size={16} />
                En progreso
              </DropdownMenuItem>
            )}

            {onStateChange && project.state !== "completed" && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClose();
                  onStateChange(project.id, "completed", project.state);
                }}
              >
                <CheckCircle2 size={16} />
                Completado
              </DropdownMenuItem>
            )}

            {onStateChange && project.state !== "not_started" && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClose();
                  onStateChange(project.id, "not_started", project.state);
                }}
              >
                <Circle size={16} />
                No iniciado
              </DropdownMenuItem>
            )}

            {canToggleVisibility && onToggleVisibility && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClose();
                  onToggleVisibility(project.id, project.is_internal);
                }}
              >
                {project.is_internal ? (
                  <>
                    <Eye size={16} />
                    Mostrar al cliente
                  </>
                ) : (
                  <>
                    <EyeOff size={16} />
                    Ocultar al cliente
                  </>
                )}
              </DropdownMenuItem>
            )}

            {onConfigureRecurrence && (
              <DropdownMenuItem
                data-tutorial={recurrenceMenuItemDataTutorial}
                sx={
                  highlightRecurrenceMenuItem
                    ? { border: `2px solid ${theme.palette.error.main}`, borderRadius: "8px" }
                    : undefined
                }
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClose();
                  onConfigureRecurrence(project);
                }}
              >
                <Settings size={16} />
                Configurar recurrencia
              </DropdownMenuItem>
            )}

            {onDelete && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClose();
                  onDelete(project.id);
                }}
                sx={{ color: "error.main" }}
              >
                <Trash2 size={16} />
                Eliminar proyecto
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </>
      )}
    </Stack>
  );
}

const captionSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  fontSize: { xs: "0.7rem", md: "0.75rem" },
  whiteSpace: "nowrap",
};

export function ProjectsTableView({
  rows,
  totalCount,
  isFetching,
  error,
  currentPage,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  isRecurrentProjectsTable,
  showClientColumn,
  emptyMessage,
  onViewProgress,
  getProjectInteractionProps,
  toolbarProps,
  getRowProps,
}) {
  const theme = useTheme();

  const columns = useMemo(() => {
    const renderers = {
      project: (project) => (
        <Box sx={{ maxWidth: { xs: 240, md: 340 }, minWidth: { md: 300 } }}>
          <Tooltip
            title={project.name || project.template_name || "Proyecto"}
            arrow
            slotProps={{ tooltip: { sx: { textAlign: "center" } } }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "0.75rem", md: "0.875rem" },
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
              }}
            >
              <FolderOpen size={13} color={theme.palette.text.secondary} style={{ flexShrink: 0 }} />
              <Box
                component="span"
                sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
              >
                {project.name || project.template_name || "Proyecto"}
              </Box>
            </Typography>
          </Tooltip>
        </Box>
      ),
      client: (project) => (
        <Tooltip title={project.client_name || "Sin cliente"} arrow>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            <Avatar
              size="sm"
              name={project.client_name || "Cliente"}
              src={getAvatarImageUrl(project.client_logo) || undefined}
            />
            <Typography
              variant="caption"
              color="text.primary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.7rem", md: "0.75rem" },
              }}
            >
              {project.client_name || "-"}
            </Typography>
          </Box>
        </Tooltip>
      ),
      state: (project) => (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Badge color={PROJECT_STATE_BADGES[project.state].color} size="sm">
            {PROJECT_STATE_BADGES[project.state].label}
          </Badge>
          {getProjectInteractionProps(project).isReadOnly && <ReadOnlyBadge />}
        </Stack>
      ),
      template: (project) => (
        <Tooltip title={project.template_name || "Proyecto sin plantilla"} arrow>
          <Typography variant="caption" sx={captionSx}>
            <Layers size={12} color={theme.palette.text.secondary} />
            {project.template_name || "Proyecto sin plantilla"}
          </Typography>
        </Tooltip>
      ),
      start: (project) => (
        <Tooltip title={formatDate(project.start_date)} arrow>
          <Typography variant="caption" sx={captionSx}>
            <Calendar size={12} color={theme.palette.text.secondary} />
            {formatDate(project.start_date)}
          </Typography>
        </Tooltip>
      ),
      created: (project) => (
        <Tooltip title={formatDate(project.created_at)} arrow>
          <Typography variant="caption" sx={captionSx}>
            <Clock size={12} color={theme.palette.text.secondary} />
            {formatDate(project.created_at)}
          </Typography>
        </Tooltip>
      ),
      visibility: (project) => {
        const isCustomProject = project.project_type === "custom";
        const visibilityLabel = !isCustomProject
          ? "-"
          : project.is_internal
            ? "Ocultado al cliente"
            : "Visible al cliente";
        return (
          <Tooltip title={visibilityLabel} arrow>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 130 }}>
              {project.is_internal && isCustomProject ? (
                <EyeOff size={12} color={theme.palette.text.secondary} />
              ) : (
                <Eye size={12} color={theme.palette.text.secondary} />
              )}
              <Typography variant="caption" sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}>
                {visibilityLabel}
              </Typography>
            </Stack>
          </Tooltip>
        );
      },
      progress: (project) => {
        const progress = project.progress_percentage || 0;
        return (
          <Tooltip title={`${Math.round(progress)}%`} arrow>
            <Stack spacing={0.5} sx={{ minWidth: 120 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.7rem", md: "0.75rem" },
                }}
              >
                {Math.round(progress)}%
              </Typography>
              <Box
                sx={{
                  height: 6,
                  borderRadius: 9999,
                  backgroundColor: theme.palette.divider,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${Math.min(Math.max(progress, 0), 100)}%`,
                    backgroundColor: theme.palette.primary.main,
                    transition: "width 0.2s ease",
                  }}
                />
              </Box>
            </Stack>
          </Tooltip>
        );
      },
      actions: (project) => (
        <ProjectActionsCell
          project={project}
          interactionProps={getProjectInteractionProps(project)}
          onViewProgress={onViewProgress}
        />
      ),
    };

    return TABLE_COLUMNS.map((column) => ({
      key: column.key,
      header: column.label,
      tooltip: column.tooltip,
      align: column.key === "actions" ? "right" : "left",
      render: renderers[column.key],
      ...(column.key === "project" && { minWidth: { xs: 240, md: 340 }, width: { md: 340 } }),
      hidden:
        (column.key === "visibility" && isRecurrentProjectsTable) ||
        (column.key === "client" && !showClientColumn),
    }));
  }, [theme, isRecurrentProjectsTable, showClientColumn, getProjectInteractionProps, onViewProgress]);

  return (
    <DataTable
      {...toolbarProps}
      columns={columns}
      rows={rows}
      loading={isFetching}
      emptyIcon={FolderOpen}
      emptyMessage={
        error
          ? "Error al cargar proyectos. Intenta nuevamente."
          : emptyMessage || "No hay proyectos disponibles"
      }
      onRowClick={(project) => onViewProgress(project)}
      getRowProps={getRowProps}
      slotProps={TABLE_SLOT_PROPS}
      tableMinWidth={1200}
      maxHeight="calc(100vh - 290px)"
      minHeight="calc(100vh - 290px)"
      page={currentPage}
      rowsPerPage={rowsPerPage}
      totalCount={totalCount}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  );
}

ProjectActionsCell.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    state: PropTypes.string,
    is_internal: PropTypes.bool,
  }).isRequired,
  interactionProps: PropTypes.shape({
    onStateChange: PropTypes.func,
    onDelete: PropTypes.func,
    onToggleVisibility: PropTypes.func,
    canToggleVisibility: PropTypes.bool,
    onConfigureRecurrence: PropTypes.func,
    forceMenuOpen: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    menuButtonDataTutorial: PropTypes.string,
    recurrenceMenuItemDataTutorial: PropTypes.string,
    highlightRecurrenceMenuItem: PropTypes.bool,
  }).isRequired,
  onViewProgress: PropTypes.func.isRequired,
};

ProjectsTableView.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalCount: PropTypes.number.isRequired,
  isFetching: PropTypes.bool,
  error: PropTypes.any,
  currentPage: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  isRecurrentProjectsTable: PropTypes.bool,
  showClientColumn: PropTypes.bool,
  emptyMessage: PropTypes.string,
  onViewProgress: PropTypes.func.isRequired,
  getProjectInteractionProps: PropTypes.func.isRequired,
  toolbarProps: PropTypes.object,
  getRowProps: PropTypes.func,
};

ProjectsTableView.defaultProps = {
  showClientColumn: true,
};
